/**
 * Shared program / à la carte monthly pricing (matches ProgramDetail.js logic).
 */

/**
 * The pooled 1:1 hours item. Renamed "1:1 Strategy Call" → "Implementation Hours" (July 2026),
 * so both names must match: the catalog doc carries the new name while existing contracts and
 * booking logs still carry the old one.
 */
export const STRATEGY_CALL_ITEM_PATTERN = /1:1\s*strat|1:1\s*strategy\s*call|strat\s*call|implementation\s*hours?/i;

export function isStrategyCallItem(item) {
  return STRATEGY_CALL_ITEM_PATTERN.test(item?.name || '');
}

/** Same rule against a bare name string (contract rows, booking logs). */
export function isStrategyCallName(name) {
  return STRATEGY_CALL_ITEM_PATTERN.test(name || '');
}

/**
 * Broad match for contract rows and deliverable logs. The catalog pattern above
 * deliberately skips a bare "Strategy Call" (no "1:1"), but legacy contract rows
 * and booking logs carry names like "Strategy Call" / "Monthly Strategy Call",
 * so deliverable lookups accept those too.
 */
export function isStrategyCallDeliverableName(name) {
  return (
    String(name || '').toLowerCase().includes('strategy call') || isStrategyCallName(name)
  );
}

export function isHiringServiceItem(item) {
  return /^hiring(\s*service)?$/i.test((item?.name || '').trim());
}

export function isOnSiteVisitItem(item) {
  return /on[- ]?site\s*visit|onsite\s*visit/i.test(item?.name || '');
}

/**
 * Items with the "unpromoted" pricing status stay on file (and keep working for
 * clients already enrolled) but are hidden from every purchase surface — the
 * à la carte shop, billing plan editing, popular add-ons, and program builders.
 * ('unpromoted' = PRICING_STATUS.unpromoted in alaCarteCatalog.js.)
 */
export function isAlaCarteItemHiddenFromPurchase(item) {
  return item?.pricingStatus === 'unpromoted';
}

/**
 * Whether a catalog item gets a card in the program builder's Deliverables grid.
 * Child features (calculators, trackers, libraries) ride along with their parent and
 * never get their own card; unpromoted items only appear once they're part of the selection.
 */
export function isProgramDeliverableCard(item, isSelected = false) {
  return (
    item?.pricingType !== 'child' &&
    !/on[- ]?site\s*visit|onsite\s*visit/i.test(item?.name || '') &&
    (!isAlaCarteItemHiddenFromPurchase(item) || isSelected)
  );
}

/**
 * Items priced per "each" (`standardPricing.unit`) are one-off units whose price covers
 * the whole engagement — e.g. the CQC Visit. Inside a program they spread across the
 * program's minimum term rather than their own catalog term, so the monthly figure
 * reflects the term the client is actually committing to (which another selected
 * service's minimum term may have raised).
 */
export function isEachUnitItem(item) {
  return String(item?.standardPricing?.unit || '').trim().toLowerCase() === 'each';
}

/**
 * Optional catalog minimum term in months (`minimumTermMonths`); `null` means none.
 * Semantics: the standard price covers the full minimum term, so inside a program
 * the item contributes price ÷ months to the monthly total.
 */
export function getItemMinimumTermMonths(item) {
  const raw = item?.minimumTermMonths;
  if (raw === '' || raw == null) return null;
  const n = parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/** Highest minimum term (months) across the selected catalog items; 0 when none set. */
export function getMaxSelectedMinimumTermMonths(selectedDeliverableIds, availableItems) {
  const ids = selectedDeliverableIds instanceof Set ? [...selectedDeliverableIds] : selectedDeliverableIds || [];
  return ids.reduce((max, itemId) => {
    const item = (availableItems || []).find((i) => i.id === itemId);
    const months = getItemMinimumTermMonths(item);
    return months != null && months > max ? months : max;
  }, 0);
}

/**
 * The whole program's minimum term: the billing-cycle floor, raised by any selected
 * service's own catalog minimum term. Mirrors the rule in CustomProgram.js; ProgramDetail.js
 * layers an extra "lite" rule on top and passes its own value in instead.
 */
export function getProgramMinimumTermMonths(selectedDeliverableIds, availableItems, billingCycleRaw) {
  const ids = selectedDeliverableIds instanceof Set ? [...selectedDeliverableIds] : selectedDeliverableIds || [];
  const items = availableItems || [];
  const bc = String(billingCycleRaw || 'monthly').toLowerCase();
  const hasTermPricedItem = ids.some((id) => items.find((i) => i.id === id)?.pricingType === 'term');
  const floor = bc === 'annual' || bc === 'year' ? 12 : hasTermPricedItem ? 6 : 1;
  return Math.max(floor, getMaxSelectedMinimumTermMonths(ids, items));
}

/**
 * Months to spread a single line's total over. "Each"-unit items follow the program's
 * minimum term; everything else keeps its own catalog term. Always >= 1, so a program
 * with no term commitment bills the line in full that month.
 */
function getLineSpreadMonths(item, programMinimumTermMonths) {
  if (isEachUnitItem(item)) {
    const n = parseInt(String(programMinimumTermMonths ?? '').trim(), 10);
    return Number.isFinite(n) && n > 1 ? n : 1;
  }
  return getItemMinimumTermMonths(item) || 1;
}

/**
 * "Always required" items (`alwaysRequired` on alaCarteItems, toggled on the item overview —
 * e.g. Portal Access) are auto-selected in every program, can't be removed, and are pinned
 * to exactly one.
 */
export function isAlwaysRequiredItem(item) {
  return item?.alwaysRequired === true;
}

/** Catalog cap from `deliverable.maxQuantity`; `null` means no limit. Always-required items cap at 1. */
export function getDeliverableMaxQuantity(item) {
  if (isAlwaysRequiredItem(item)) return 1;
  const raw = item?.deliverable?.maxQuantity;
  if (raw === '' || raw == null) return null;
  const n = parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/** Clamp a desired quantity to [1, max] when the catalog defines a max. */
export function clampDeliverableQuantity(item, rawQty) {
  const q = Math.max(1, parseInt(String(rawQty), 10) || 1);
  const max = getDeliverableMaxQuantity(item);
  if (max == null) return q;
  return Math.min(max, q);
}

/**
 * A case-by-case item has no catalog price — it only carries a number once a bid has been
 * published for that specific account. `bidPricesByCatalogItemId` maps catalog item id to
 * that published `clientListPrice`; see `fetchPublishedBidsForAccount`.
 * @returns {number} monthly contribution, spread on the same rules as a standard line
 */
export function monthlyAmountForCaseByCaseBid(item, bidPricesByCatalogItemId, programMinimumTermMonths) {
  const price = parseFloat(bidPricesByCatalogItemId?.[item?.id]);
  if (!Number.isFinite(price) || price <= 0) return 0;
  return price / getLineSpreadMonths(item, programMinimumTermMonths);
}

/**
 * A case-by-case item with no published bid for this account: it has no price at all yet, so it
 * must not move any money figure — it surfaces as a "+ N to be priced" count instead.
 */
export function isUnpricedCaseByCaseItem(item, bidPricesByCatalogItemId) {
  if (item?.pricingType !== 'caseByCase') return false;
  const price = parseFloat(bidPricesByCatalogItemId?.[item.id]);
  return !Number.isFinite(price) || price <= 0;
}

/**
 * Selected case-by-case items still awaiting a published bid for this account. These
 * contribute nothing to the monthly figure — they surface as a "+ N to be priced" count.
 */
export function countUnpricedCaseByCaseItems(selectedDeliverableIds, availableItems, bidPricesByCatalogItemId) {
  const ids = selectedDeliverableIds instanceof Set ? [...selectedDeliverableIds] : selectedDeliverableIds || [];
  return ids.filter((itemId) => {
    const item = (availableItems || []).find((i) => i.id === itemId);
    return isUnpricedCaseByCaseItem(item, bidPricesByCatalogItemId);
  }).length;
}

export function getItemUnitPrice(item) {
  if (!item) return 0;
  if (item.pricingType === 'child') return 0;
  if (item.pricingType === 'caseByCase') return null;
  if (item.standardPricing?.cost != null) {
    return parseFloat(item.standardPricing.cost) || 0;
  }
  if (item.packages?.length > 0) {
    const pkg = item.packages[0];
    const total = (pkg.items || []).reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0);
    return total;
  }
  return 0;
}

export function getTermDivisors(billingCycleRaw) {
  const bc = String(billingCycleRaw || 'monthly').toLowerCase();
  const minimumTerm = bc === 'annual' || bc === 'year' ? 12 : 6;
  const termDivisor = minimumTerm === 6 ? 6 : 12;
  return { minimumTerm, termDivisor };
}

/**
 * Legacy programs (Lite, Plus, Pro and every program sold before custom-only pricing) include the
 * first Implementation Hour, so only hours past the first are billed. New custom programs bill
 * every hour; callers opt in with { billEveryImplementationHour: true }.
 */
export function implementationHoursBillableQty(item, quantity, { billEveryImplementationHour = false } = {}) {
  if (!isStrategyCallItem(item) || billEveryImplementationHour) return quantity;
  return Math.max(0, quantity - 1);
}

/**
 * Single selected line's contribution to monthly recurring (same rules as ProgramDetail totalPriceFromSelection reduce body).
 */
export function monthlyAmountForSelectedLine(item, itemId, deliverableQuantities, billingCycleRaw, programMinimumTermMonths, options = {}) {
  const { termDivisor } = getTermDivisors(billingCycleRaw);
  const spreadMonths = getLineSpreadMonths(item, programMinimumTermMonths);
  if (isHiringServiceItem(item)) {
    const amount = getItemUnitPrice(item);
    if (amount == null) return 0;
    const qty = clampDeliverableQuantity(item, deliverableQuantities[itemId] ?? 1);
    if (item?.pricingType === 'term') {
      return (amount / termDivisor) * qty;
    }
    return (amount / spreadMonths) * qty;
  }
  if (item?.pricingType === 'term') {
    const amount = getItemUnitPrice(item);
    if (amount == null || amount === 0) return 0;
    return amount / termDivisor;
  }
  const unitPrice = getItemUnitPrice(item);
  if (unitPrice == null) return 0;
  const deliverablePeriod = (item?.deliverable?.period || '').toLowerCase();
  const hasQuantityDeliverable =
    item?.hasDeliverable && ['month', 'one time', 'onetime'].includes(deliverablePeriod);
  const rawQty = hasQuantityDeliverable
    ? deliverableQuantities[itemId] ?? (parseInt(item?.deliverable?.quantity, 10) || 1)
    : 1;
  const quantity = hasQuantityDeliverable ? clampDeliverableQuantity(item, rawQty) : 1;
  const billableQty = implementationHoursBillableQty(item, quantity, options);
  const lineTotal = unitPrice * billableQty;
  return lineTotal / spreadMonths;
}

/**
 * Popular add-ons (portal cards / selection): same quantity and strategy-call billable rules as
 * {@link monthlyAmountForSelectedLine}, but **no term amortization** (never divides by termDivisor).
 * @returns {number|null} null for case-by-case
 */
export function popularAddOnLineAmount(item, itemId, deliverableQuantities) {
  if (!item || item.pricingType === 'child') return 0;
  if (item.pricingType === 'caseByCase') return null;

  if (isHiringServiceItem(item)) {
    const amount = getItemUnitPrice(item);
    if (amount == null) return 0;
    const qty = clampDeliverableQuantity(item, deliverableQuantities[itemId] ?? 1);
    return amount * qty;
  }

  if (item?.pricingType === 'term') {
    const amount = getItemUnitPrice(item);
    if (amount == null || amount === 0) return 0;
    return amount;
  }

  const unitPrice = getItemUnitPrice(item);
  if (unitPrice == null) return 0;
  const deliverablePeriod = (item?.deliverable?.period || '').toLowerCase();
  const hasQuantityDeliverable =
    item?.hasDeliverable && ['month', 'one time', 'onetime'].includes(deliverablePeriod);
  const rawQty = hasQuantityDeliverable
    ? deliverableQuantities[itemId] ?? (parseInt(item?.deliverable?.quantity, 10) || 1)
    : 1;
  const quantity = hasQuantityDeliverable ? clampDeliverableQuantity(item, rawQty) : 1;
  const billableQty = isStrategyCallItem(item) ? Math.max(0, quantity - 1) : quantity;
  return unitPrice * billableQty;
}

export function computeTotalPriceFromSelection(
  selectedDeliverableIds,
  deliverableQuantities,
  availableItems,
  billingCycleRaw,
  programMinimumTermMonths,
  options = {}
) {
  const ids = selectedDeliverableIds instanceof Set ? [...selectedDeliverableIds] : selectedDeliverableIds;
  const term = programMinimumTermMonths ?? getProgramMinimumTermMonths(ids, availableItems, billingCycleRaw);
  return ids.reduce((sum, itemId) => {
    const item = availableItems.find((i) => i.id === itemId);
    return sum + monthlyAmountForSelectedLine(item, itemId, deliverableQuantities || {}, billingCycleRaw, term, options);
  }, 0);
}

export function buildProgramItemIdsSet(programFeatures, availableItems) {
  const ids = new Set();
  Object.keys(programFeatures || {}).forEach((itemName) => {
    const item = availableItems.find((i) => (i.name || '').trim() === (itemName || '').trim());
    // A feature entry can outlive its catalog item's promotion (the programs template
    // hides unpromoted items, so the stale entry is invisible there) — never count it.
    if (item && !isOnSiteVisitItem(item) && !isAlaCarteItemHiddenFromPurchase(item)) ids.add(item.id);
  });
  return ids;
}

export function computeAddOnPricePerMonth(
  selectedDeliverableIds,
  deliverableQuantities,
  availableItems,
  billingCycleRaw,
  programItemIds,
  programMinimumTermMonths,
  options = {}
) {
  const ids = selectedDeliverableIds instanceof Set ? [...selectedDeliverableIds] : selectedDeliverableIds;
  const baseIds = programItemIds instanceof Set ? programItemIds : new Set(programItemIds || []);
  // Derived from the whole selection, not just the add-ons: a base program service's
  // minimum term still raises the term an add-on spreads over.
  const term = programMinimumTermMonths ?? getProgramMinimumTermMonths(ids, availableItems, billingCycleRaw);
  return ids
    .filter((itemId) => !baseIds.has(itemId))
    .reduce((sum, itemId) => {
      const item = availableItems.find((i) => i.id === itemId);
      return sum + monthlyAmountForSelectedLine(item, itemId, deliverableQuantities || {}, billingCycleRaw, term, options);
    }, 0);
}

/**
 * "Customized" flips pricing off the program's set price and onto the à la carte sum, so only
 * items that actually move money count. Always-included and strategy-call lines are exempt for
 * that reason — and so is a case-by-case line with no published bid, which has no price yet.
 */
export function computeIsCustomized(
  selectedDeliverableIds,
  programItemIds,
  deliverableQuantities,
  availableItems,
  programFeatures,
  bidPricesByCatalogItemId
) {
  const selected = selectedDeliverableIds instanceof Set ? selectedDeliverableIds : new Set(selectedDeliverableIds || []);
  const progIds = programItemIds instanceof Set ? programItemIds : new Set(programItemIds || []);
  const feats = programFeatures || {};

  const movesNoMoney = (item) =>
    item?.pricingType === 'alwaysIncluded' ||
    isStrategyCallItem(item) ||
    isUnpricedCaseByCaseItem(item, bidPricesByCatalogItemId);

  const hasAdditions = [...selected].some((id) => {
    const item = availableItems.find((i) => i.id === id);
    if (movesNoMoney(item)) return false;
    return !progIds.has(id);
  });
  const hasRemovals = [...progIds].some((id) => {
    const item = availableItems.find((i) => i.id === id);
    if (movesNoMoney(item)) return false;
    return !selected.has(id);
  });
  const hasQuantityChanges = [...progIds].some((itemId) => {
    const item = availableItems.find((i) => i.id === itemId);
    if (!item) return false;
    const featureData = feats[item.name];
    const expectedQty =
      typeof featureData === 'object' && featureData?.quantity != null
        ? parseInt(featureData.quantity, 10)
        : parseInt(featureData, 10);
    if (Number.isNaN(expectedQty) || expectedQty < 1) return false;
    const actualQty = clampDeliverableQuantity(
      item,
      deliverableQuantities[itemId] ?? (parseInt(item?.deliverable?.quantity, 10) || 1)
    );
    return actualQty !== expectedQty;
  });
  return hasAdditions || hasRemovals || hasQuantityChanges;
}

export function computeTotalPricePerMonth({
  isCustomized,
  totalPriceFromSelection,
  basePricePerMonth,
  addOnPricePerMonth,
}) {
  const base = parseFloat(basePricePerMonth) || 0;
  const addOn = addOnPricePerMonth || 0;
  const fromSel = totalPriceFromSelection || 0;
  return isCustomized ? (fromSel || base + addOn) : base + addOn;
}

export function computeDisplayMonthly(totalPricePerMonth, billingCycleRaw, billingCycles) {
  const bc = String(billingCycleRaw || 'monthly').toLowerCase();
  const t = Math.round(totalPricePerMonth || 0);
  if (bc === 'annual' && billingCycles?.annual?.discount != null) {
    return Math.round(t * (1 - billingCycles.annual.discount / 100));
  }
  if ((bc === 'quarterly' || bc === 'quarter') && billingCycles?.quarterly?.discount != null) {
    return Math.round(t * (1 - billingCycles.quarterly.discount / 100));
  }
  return t;
}

/**
 * Custom program monthly base from account flags (matches ClientOverview / CustomProgram rules).
 */
export function computeCustomProgramMonthlyBaseFromAccount(account, items, billingCycleRaw) {
  const ids = account?.customProgramDeliverables;
  if (!Array.isArray(ids) || ids.length === 0 || !items?.length) return null;
  const quantities = account.customProgramDeliverableQuantities || {};
  const { termDivisor } = getTermDivisors(billingCycleRaw);
  const programTerm = getProgramMinimumTermMonths(ids, items, billingCycleRaw);

  let sum = 0;
  let skippedCaseByCaseOnly = false;
  for (const itemId of ids) {
    const item = items.find((i) => i.id === itemId);
    if (!item) continue;
    if (item.pricingType === 'caseByCase') {
      skippedCaseByCaseOnly = true;
      continue;
    }
    const spreadMonths = getLineSpreadMonths(item, programTerm);
    if (/hiring\s*service|hiring/i.test(item?.name || '')) {
      const amount = getItemUnitPrice(item);
      if (amount == null) continue;
      const qty = clampDeliverableQuantity(item, quantities[itemId] ?? 1);
      if (item?.pricingType === 'term') {
        sum += (amount / termDivisor) * qty;
      } else {
        sum += (amount / spreadMonths) * qty;
      }
      continue;
    }
    if (item?.pricingType === 'term') {
      const amount = getItemUnitPrice(item);
      if (amount == null || amount === 0) continue;
      sum += amount / termDivisor;
      continue;
    }
    const unitPrice = getItemUnitPrice(item);
    if (unitPrice == null) continue;
    const deliverablePeriod = (item?.deliverable?.period || '').toLowerCase();
    const hasQuantityDeliverable =
      item?.hasDeliverable && ['month', 'one time', 'onetime'].includes(deliverablePeriod);
    const rawQty = hasQuantityDeliverable
      ? quantities[itemId] ?? (parseInt(item?.deliverable?.quantity, 10) || 1)
      : 1;
    const quantity = hasQuantityDeliverable ? clampDeliverableQuantity(item, rawQty) : 1;
    const isStrategyCall = isStrategyCallItem(item);
    const billableQty = isStrategyCall ? Math.max(0, quantity - 1) : quantity;
    const lineTotal = unitPrice * billableQty;
    sum += lineTotal / spreadMonths;
  }
  if (sum <= 0 && skippedCaseByCaseOnly) return null;
  return sum > 0 ? sum : null;
}
